import { extname, join } from "path";
import { brotliCompressSync, gzipSync } from "zlib";
import { stat } from "fs/promises";

import { resolveStaticFileRequest } from "./static-file-routing";

type StaticEncoding = "br" | "gzip";
type CachedEncodedFile = { body: Buffer; createdAt: number };

const compressibleStaticExtensions = new Set([
  ".css",
  ".csv",
  ".htm",
  ".html",
  ".js",
  ".json",
  ".mjs",
  ".svg",
  ".txt",
  ".wasm",
  ".webmanifest",
  ".xml",
]);
const encodedFileCache = new Map<string, CachedEncodedFile>();
const encodedFileCacheTtlMs = 10 * 60 * 1000;
const encodedFileCacheMaxEntries = 240;

function injectHtmlHeadTag(html: string, tag: string, skipMarker: string): string {
  if (!tag || html.includes(skipMarker)) return html;
  const headClose = /<\/head\s*>/i;
  if (headClose.test(html)) return html.replace(headClose, `  ${tag}\n</head>`);
  const htmlOpen = /<html(\s[^>]*)?>/i;
  if (htmlOpen.test(html)) return html.replace(htmlOpen, (match) => `${match}\n<head>\n  ${tag}\n</head>`);
  return `${tag}\n${html}`;
}

function acceptsEncoding(value: string, encoding: StaticEncoding): boolean {
  const tokens = value
    .toLowerCase()
    .split(",")
    .map((part) => part.trim());
  return tokens.some((token) => {
    if (!token.startsWith(encoding)) return false;
    const q = token.match(/;\s*q=([0-9.]+)/)?.[1];
    return q === undefined || Number(q) > 0;
  });
}

function requestHeader(c: any, name: string): string {
  const direct = c?.req?.header?.(name);
  if (typeof direct === "string") return direct;
  const fromHeaders = c?.req?.headers?.get?.(name);
  return typeof fromHeaders === "string" ? fromHeaders : "";
}

function preferredStaticEncoding(acceptEncoding: string, filePath: string): StaticEncoding | null {
  if (!compressibleStaticExtensions.has(extname(filePath).toLowerCase())) return null;
  if (acceptsEncoding(acceptEncoding, "br")) return "br";
  if (acceptsEncoding(acceptEncoding, "gzip")) return "gzip";
  return null;
}

function pruneEncodedFileCache(now = Date.now()): void {
  for (const [key, value] of encodedFileCache) {
    if (now - value.createdAt > encodedFileCacheTtlMs) encodedFileCache.delete(key);
  }
  while (encodedFileCache.size > encodedFileCacheMaxEntries) {
    const oldest = encodedFileCache.keys().next().value as string | undefined;
    if (!oldest) break;
    encodedFileCache.delete(oldest);
  }
}

async function encodedStaticFile(filePath: string, encoding: StaticEncoding, originalSize: number): Promise<Buffer | null> {
  const stats = await stat(filePath).catch(() => null);
  const cacheKey = `${filePath}:${stats?.mtimeMs ?? 0}:${originalSize}:${encoding}`;
  pruneEncodedFileCache();
  const cached = encodedFileCache.get(cacheKey);
  if (cached) return cached.body;

  const input = Buffer.from(await Bun.file(filePath).arrayBuffer());
  const output = encoding === "br" ? brotliCompressSync(input) : gzipSync(input);
  if (output.byteLength >= input.byteLength) return null;
  encodedFileCache.set(cacheKey, { body: output, createdAt: Date.now() });
  return output;
}

function encodeStaticBuffer(input: Buffer, encoding: StaticEncoding | null): { body: Buffer; encoding: StaticEncoding | null } {
  if (!encoding || input.byteLength <= 0) return { body: input, encoding: null };
  const output = encoding === "br" ? brotliCompressSync(input) : gzipSync(input);
  if (output.byteLength >= input.byteLength) return { body: input, encoding: null };
  return { body: output, encoding };
}

function bufferToBlob(buffer: Buffer): Blob {
  const arrayBuffer = buffer.buffer.slice(buffer.byteOffset, buffer.byteOffset + buffer.byteLength) as ArrayBuffer;
  return new Blob([arrayBuffer]);
}

export async function serveStaticFile(params: {
  c: any;
  dataDir: string;
  pathname: string;
  appName?: string;
  recordBytes?: (bytes: number) => void;
  htmlHeadInjection?: string;
  htmlHeadInjectionSkipMarker?: string;
}): Promise<Response | unknown> {
  const staticDir = join(params.dataDir, "static");
  const resolution = await resolveStaticFileRequest({
    staticDir,
    pathname: params.pathname,
    exists: async (path) => await Bun.file(path).exists(),
  });

  if (resolution.kind === "forbidden") return params.c.json({ error: "Forbidden" }, 403);
  if (resolution.kind === "not_found") {
    return params.c.json({ error: `Static file not found: ${params.pathname}` }, 404);
  }

  const file = Bun.file(resolution.filePath);
  const fileSize = file.size;
  const requestMethod = String(params.c?.req?.method ?? "").toUpperCase();
  const encoding = preferredStaticEncoding(requestHeader(params.c, "Accept-Encoding"), resolution.filePath);
  const isHtml = [".html", ".htm"].includes(extname(resolution.filePath).toLowerCase());
  const shouldInjectHtml = Boolean(params.htmlHeadInjection && isHtml);
  let responseBody: Blob;
  let responseBytes = fileSize;
  let responseEncoding: StaticEncoding | null = null;

  if (shouldInjectHtml) {
    const originalHtml = await file.text();
    const html = injectHtmlHeadTag(
      originalHtml,
      params.htmlHeadInjection ?? "",
      params.htmlHeadInjectionSkipMarker ?? params.htmlHeadInjection ?? "",
    );
    const encoded = encodeStaticBuffer(Buffer.from(html), encoding);
    responseBody = bufferToBlob(encoded.body);
    responseBytes = encoded.body.byteLength;
    responseEncoding = encoded.encoding;
  } else {
    const encodedBody = encoding && Number.isSafeInteger(fileSize) && fileSize > 0
      ? await encodedStaticFile(resolution.filePath, encoding, fileSize)
      : null;
    responseBody = encodedBody ? bufferToBlob(encodedBody) : file;
    responseBytes = encodedBody?.byteLength ?? fileSize;
    responseEncoding = encodedBody ? encoding : null;
  }
  if (requestMethod !== "HEAD" && Number.isSafeInteger(responseBytes) && responseBytes > 0) {
    params.recordBytes?.(responseBytes);
  }
  const headers = new Headers({ "Cache-Control": resolution.cacheControl });
  if (responseEncoding) {
    headers.set("Content-Encoding", responseEncoding);
    headers.set("Vary", "Accept-Encoding");
  }
  if (file.type) headers.set("Content-Type", file.type);
  if (Number.isSafeInteger(responseBytes) && responseBytes >= 0) {
    headers.set("Content-Length", String(responseBytes));
  }
  return new Response(requestMethod === "HEAD" ? null : responseBody, {
    headers,
  });
}
